//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//

import DataUrlConstants from 'utils/DataUrlConstants';
import { makeUrl, MakeUrlTokens } from 'utils/UrlUtil';

import { injectEndpoints, tags } from '../api';
import {
  CreateSyncAISessionResponse,
  CustomActionPythonPayload,
  CustomActionPythonSyncAIGeneratedPayload,
  SendSyncAIMessageRequest,
  SendSyncAIMessageResponse,
} from './types';
import { transformToCustomActionPython, transformToCustomActionPythonSyncAI } from './util';

export const customActionPythonApi = injectEndpoints({
  endpoints: (builder) => ({
    // Get a Python custom action
    getCustomActionPython: builder.query<CustomActionPythonPayload, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_ITEM, { functionId: params.functionId } as MakeUrlTokens),
          method: 'GET',
        };
      },
      transformResponse: transformToCustomActionPython,
      providesTags: (result) => (result?.id ? [tags.CustomActionPython(result.id)] : []),
    }),
    // Get Python custom action draft
    getCustomActionPythonDraft: builder.query<CustomActionPythonPayload, { functionId: string; action?: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_GET_DRAFT, {
            functionId: params.functionId,
          } as MakeUrlTokens),
          method: 'GET',
        };
      },
      transformResponse: transformToCustomActionPython,
    }),
    // Create a draft of a Python custom action
    createCustomActionPython: builder.mutation<
      CustomActionPythonPayload,
      CustomActionPythonPayload & Record<string, string>
    >({
      query: (params) => {
        const { id, ...body } = params;
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_CREATE_DRAFT),
          method: 'POST',
          body,
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: [tags.CustomActionList],
    }),
    // Update a Python custom action
    updateCustomActionPython: builder.mutation<
      CustomActionPythonPayload,
      CustomActionPythonPayload & Record<string, string>
    >({
      query: (params) => {
        const { id, ...body } = params;
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_UPDATE_DRAFT, { functionId: id } as MakeUrlTokens),
          method: 'PUT',
          body,
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: (result) => [tags.CustomActionList, ...(result?.id ? [tags.CustomActionPython(result.id)] : [])],
    }),
    // Publish a Python custom action
    publishCustomActionPython: builder.mutation<void, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_PUBLISH, {
            functionId: params.functionId,
          } as MakeUrlTokens),
          method: 'POST',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
    // Create a draft from a published Python custom action
    createCustomActionPythonDraft: builder.mutation<CustomActionPythonPayload, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_NEW_DRAFT, params as MakeUrlTokens),
          method: 'POST',
        };
      },
      transformResponse: transformToCustomActionPython,
      invalidatesTags: [tags.CustomActionList],
    }),
    discardCustomActionPythonDraft: builder.mutation<void, { functionId: string }>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_DISCARD_DRAFT, params as MakeUrlTokens),
          method: 'POST',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
    // Delete a Python custom action
    deleteCustomActionPython: builder.mutation<void, Record<string, string>>({
      query: (params) => {
        return {
          url: makeUrl(DataUrlConstants.CUSTOM_ACTION_PYTHON_DELETE, params),
          method: 'DELETE',
        };
      },
      invalidatesTags: [tags.CustomActionList],
    }),
    // SyncAI conversational endpoints (assistanceType=CUSTOM_PYTHON_ACTION).
    createSyncAISession: builder.mutation<CreateSyncAISessionResponse, void>({
      query: () => ({
        url: DataUrlConstants.SYNC_AI_SESSIONS,
        method: 'POST',
        params: {
          assistanceType: 'CUSTOM_PYTHON_ACTION',
          provider: 'OPENAI',
        },
      }),
    }),
    sendSyncAIMessage: builder.mutation<SendSyncAIMessageResponse, SendSyncAIMessageRequest>({
      query: ({ sessionId, message, currentDefinition }) => ({
        url: makeUrl(DataUrlConstants.SYNC_AI_SESSION_MESSAGES, { sessionId } as MakeUrlTokens),
        method: 'POST',
        body: {
          message,
          ...(currentDefinition ? { metadata: { currentDefinition: JSON.stringify(currentDefinition) } } : {}),
        },
      }),
      transformResponse: (response: any): SendSyncAIMessageResponse => {
        const data = response?.message?.data ?? {};
        const operation =
          typeof data.operation === 'string'
            ? data.operation
            : typeof response?.message?.operationType === 'string'
            ? response.message.operationType
            : undefined;
        const generated =
          operation === 'GENERATE'
            ? {
                ...transformToCustomActionPythonSyncAI({
                  displayName: data.displayName,
                  apiName: data.apiName,
                  helpText: data.helpText,
                  requirements: data.requirements,
                  code: data.code,
                  inputVariables: data.inputVariables,
                }),
                testCases: Array.isArray(data.testCases) ? data.testCases : undefined,
              }
            : undefined;
        return {
          generated,
          summary: typeof data.message === 'string' ? data.message : undefined,
          operation,
        };
      },
    }),
    // Sharing is handled by the HTTP useShareCustomActionMutation — the
    // backend endpoint at /api/v1/actions/share/{id} accepts Python action ids.
  }),
});

export const {
  useGetCustomActionPythonQuery,
  useLazyGetCustomActionPythonQuery,
  useGetCustomActionPythonDraftQuery,
  useLazyGetCustomActionPythonDraftQuery,
  useCreateCustomActionPythonMutation,
  useCreateCustomActionPythonDraftMutation,
  useDiscardCustomActionPythonDraftMutation,
  useDeleteCustomActionPythonMutation,
  useCreateSyncAISessionMutation,
  useSendSyncAIMessageMutation,
  useUpdateCustomActionPythonMutation,
  usePublishCustomActionPythonMutation,
} = customActionPythonApi;
